<script lang="ts">
  import { Link, router } from "@inertiajs/svelte";
  import { untrack } from "svelte";
  import type { UserProfile } from "../../lib/api";
  import { usersApi } from "../../lib/api";
  import { addNotification } from "../../lib/stores";
  import Layout from "../../components/layout/Layout.svelte";
  import Modal from "../../components/overlay/Modal.svelte";

  let { profile, user = null }: {
    profile: UserProfile & { email?: string; phone?: string; locale?: string };
    user?: { id: string; name: string; email: string; avatarUrl?: string } | null;
  } = $props();

  let formData = $state(untrack(() => ({
    name: profile.name || "",
    nickname: profile.nickname || "",
    bio: profile.bio || "",
    avatar_url: profile.avatar_url || "",
  })));

  let passwordData = $state({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  let loading = $state(false);
  let showPasswordModal = $state(false);
  let uploadingAvatar = $state(false);
  let nicknameChecking = $state(false);
  let nicknameAvailable = $state<boolean | null>(null);
  let nicknameCheckTimeout: ReturnType<typeof setTimeout> | null = null;

  // ========== S3 Upload (더미 구현) ==========
  interface S3UploadResult {
    success: boolean;
    url?: string;
    error?: string;
  }

  async function uploadToS3(file: File): Promise<S3UploadResult> {
    // 더미 S3 업로드 구현
    // 실제 구현 시: AWS SDK 또는 presigned URL을 사용하여 업로드
    return new Promise((resolve) => {
      setTimeout(() => {
        // 더미로 Data URL 반환 (실제로는 S3 URL이 반환됨)
        const reader = new FileReader();
        reader.onload = (e) => {
          resolve({
            success: true,
            url: e.target?.result as string,
            // 실제 S3 URL 예시: `https://triphelper-bucket.s3.ap-northeast-2.amazonaws.com/avatars/${file.name}`
          });
        };
        reader.onerror = () => {
          resolve({ success: false, error: "파일 읽기 실패" });
        };
        reader.readAsDataURL(file);
      }, 500); // 업로드 시뮬레이션 딜레이
    });
  }

  // ========== 닉네임 중복 검증 ==========
  async function checkNicknameAvailability(nickname: string) {
    if (!nickname || nickname.length < 2) {
      nicknameAvailable = null;
      return;
    }

    // 현재 닉네임과 같으면 검증 스킵
    if (nickname === profile.nickname) {
      nicknameAvailable = true;
      return;
    }

    nicknameChecking = true;
    try {
      // 더미 API 호출 - 실제 구현 시 서버 API 사용
      const response = await fetch(`/api/v1/users/check_nickname?nickname=${encodeURIComponent(nickname)}`);

      if (response.ok) {
        const data = await response.json();
        nicknameAvailable = data.available;
      } else {
        // API가 없으면 더미로 사용 가능으로 처리
        nicknameAvailable = true;
      }
    } catch {
      // 에러 시 더미로 사용 가능으로 처리
      nicknameAvailable = true;
    } finally {
      nicknameChecking = false;
    }
  }

  function handleNicknameInput(event: Event) {
    const input = event.target as HTMLInputElement;
    formData.nickname = input.value;

    // 디바운스 처리
    if (nicknameCheckTimeout) {
      clearTimeout(nicknameCheckTimeout);
    }
    nicknameAvailable = null;
    nicknameCheckTimeout = setTimeout(() => {
      checkNicknameAvailability(formData.nickname);
    }, 500);
  }

  async function handleSubmit() {
    // 닉네임 중복 검증
    if (nicknameAvailable === false) {
      addNotification("error", "이미 사용 중인 닉네임입니다.");
      return;
    }

    loading = true;
    try {
      const result = await usersApi.updateMe(formData);
      if (result.success) {
        addNotification("success", "프로필이 업데이트되었습니다.");
        router.reload();
      }
    } catch (error: any) {
      addNotification("error", error.response?.data?.errors?.[0]?.message || "프로필 업데이트에 실패했습니다.");
    } finally {
      loading = false;
    }
  }

  async function handlePasswordChange() {
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      addNotification("error", "새 비밀번호가 일치하지 않습니다.");
      return;
    }

    if (passwordData.newPassword.length < 8) {
      addNotification("error", "비밀번호는 8자 이상이어야 합니다.");
      return;
    }

    loading = true;
    try {
      const result = await usersApi.updatePassword(
        passwordData.currentPassword,
        passwordData.newPassword,
        passwordData.confirmPassword
      );
      if (result.success) {
        addNotification("success", "비밀번호가 변경되었습니다.");
        showPasswordModal = false;
        passwordData = { currentPassword: "", newPassword: "", confirmPassword: "" };
      }
    } catch (error: any) {
      addNotification("error", error.response?.data?.errors?.[0]?.message || "비밀번호 변경에 실패했습니다.");
    } finally {
      loading = false;
    }
  }

  async function handleAvatarUpload(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;

    // 파일 크기 검증 (5MB 제한)
    if (file.size > 5 * 1024 * 1024) {
      addNotification("error", "이미지 크기는 5MB 이하여야 합니다.");
      return;
    }

    // 파일 타입 검증
    if (!file.type.startsWith("image/")) {
      addNotification("error", "이미지 파일만 업로드할 수 있습니다.");
      return;
    }

    uploadingAvatar = true;
    try {
      const result = await uploadToS3(file);
      if (result.success && result.url) {
        formData.avatar_url = result.url;
        addNotification("success", "프로필 사진이 업로드되었습니다.");
      } else {
        addNotification("error", result.error || "업로드에 실패했습니다.");
      }
    } catch {
      addNotification("error", "업로드 중 오류가 발생했습니다.");
    } finally {
      uploadingAvatar = false;
    }
  }
</script>

<Layout {user}>
  <div class="min-h-screen bg-app py-8">
    <div class="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
      <!-- Header -->
      <div class="flex items-center mb-8">
        <Link href={`/users/${profile.id}`} class="text-muted hover:text-secondary mr-4">
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
          </svg>
        </Link>
        <h1 class="text-2xl font-bold text-primary">프로필 수정</h1>
      </div>

      <!-- Profile Form -->
      <form onsubmit={(e) => { e.preventDefault(); handleSubmit(); }} class="bg-surface rounded-2xl shadow-sm p-6 space-y-6">
        <!-- Avatar -->
        <div class="flex flex-col items-center">
          <div class="relative group">
            {#if formData.avatar_url}
              <img
                src={formData.avatar_url}
                alt="프로필 사진"
                class="w-24 h-24 rounded-full object-cover border-4 border"
              />
            {:else}
              <div class="w-24 h-24 rounded-full bg-gradient-to-br from-blue-400 to-cyan-400 flex items-center justify-center text-white text-3xl font-bold border-4 border">
                {formData.name.charAt(0) || "?"}
              </div>
            {/if}
            <label class="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer {uploadingAvatar ? 'opacity-100' : ''}">
              {#if uploadingAvatar}
                <svg class="animate-spin w-6 h-6 text-white" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
              {:else}
                <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              {/if}
              <input
                type="file"
                accept="image/*"
                class="hidden"
                onchange={handleAvatarUpload}
                disabled={uploadingAvatar}
              />
            </label>
          </div>
          <p class="text-sm text-muted mt-2">
            {#if uploadingAvatar}
              업로드 중...
            {:else}
              클릭하여 사진 변경 (최대 5MB)
            {/if}
          </p>
        </div>

        <!-- Name -->
        <div>
          <label for="name" class="block text-sm font-medium text-secondary mb-1">이름</label>
          <input
            id="name"
            type="text"
            bind:value={formData.name}
            class="w-full px-4 py-3 rounded-xl border border-bright focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all"
            placeholder="이름을 입력하세요"
            required
          />
        </div>

        <!-- Nickname -->
        <div>
          <label for="nickname" class="block text-sm font-medium text-secondary mb-1">닉네임</label>
          <div class="relative">
            <span class="absolute left-4 top-1/2 -translate-y-1/2 text-muted">@</span>
            <input
              id="nickname"
              type="text"
              value={formData.nickname}
              oninput={handleNicknameInput}
              class="w-full pl-8 pr-10 py-3 rounded-xl border transition-all {nicknameAvailable === false ? 'border-red-500 focus:border-red-500 focus:ring-red-200' : nicknameAvailable === true ? 'border-green-500 focus:border-green-500 focus:ring-green-200' : 'border-bright focus:border-blue-500 focus:ring-blue-200'}"
              placeholder="닉네임을 입력하세요"
              pattern="[a-zA-Z0-9가-힣_]+"
            />
            <!-- 닉네임 검증 상태 표시 -->
            {#if nicknameChecking}
              <div class="absolute right-3 top-1/2 -translate-y-1/2">
                <svg class="animate-spin w-5 h-5 text-muted" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
              </div>
            {:else if nicknameAvailable === true}
              <div class="absolute right-3 top-1/2 -translate-y-1/2">
                <svg class="w-5 h-5 text-success" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                </svg>
              </div>
            {:else if nicknameAvailable === false}
              <div class="absolute right-3 top-1/2 -translate-y-1/2">
                <svg class="w-5 h-5 text-error" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
            {/if}
          </div>
          <div class="flex justify-between mt-1">
            <p class="text-xs text-muted">한글, 영문, 숫자, 밑줄(_)만 사용 가능</p>
            {#if nicknameAvailable === false}
              <p class="text-xs text-error">이미 사용 중인 닉네임입니다</p>
            {:else if nicknameAvailable === true && formData.nickname !== profile.nickname}
              <p class="text-xs text-success">사용 가능한 닉네임입니다</p>
            {/if}
          </div>
        </div>

        <!-- Bio -->
        <div>
          <label for="bio" class="block text-sm font-medium text-secondary mb-1">소개</label>
          <textarea
            id="bio"
            bind:value={formData.bio}
            rows="3"
            class="w-full px-4 py-3 rounded-xl border border-bright focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all resize-none"
            placeholder="자기소개를 입력하세요"
            maxlength="500"
          ></textarea>
          <p class="text-xs text-muted mt-1">{formData.bio.length}/500</p>
        </div>

        <!-- Submit -->
        <div class="flex gap-3 pt-4">
          <button
            type="submit"
            disabled={loading}
            class="flex-1 py-3 bg-gradient-to-r from-blue-600 to-cyan-500 text-white rounded-xl font-medium hover:shadow-lg transition-all disabled:opacity-50"
          >
            {loading ? "저장 중..." : "저장하기"}
          </button>
        </div>
      </form>

      <!-- Security Section -->
      <div class="bg-surface rounded-2xl shadow-sm p-6 mt-6">
        <h2 class="text-lg font-semibold text-primary mb-4">보안</h2>
        <button
          onclick={() => showPasswordModal = true}
          class="w-full flex items-center justify-between p-4 rounded-xl border border-bright hover:border-blue-500 transition-colors"
        >
          <div class="flex items-center gap-3">
            <div class="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
              <svg class="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <div class="text-left">
              <div class="font-medium text-primary">비밀번호 변경</div>
              <div class="text-sm text-muted">계정 보안을 위해 주기적으로 변경하세요</div>
            </div>
          </div>
          <svg class="w-5 h-5 text-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>
    </div>
  </div>

  <!-- Password Change Modal -->
  <Modal show={showPasswordModal} title="비밀번호 변경" onClose={() => showPasswordModal = false}>
    <form onsubmit={(e) => { e.preventDefault(); handlePasswordChange(); }} class="space-y-4">
      <div>
        <label for="currentPassword" class="block text-sm font-medium text-secondary mb-1">현재 비밀번호</label>
        <input
          id="currentPassword"
          type="password"
          bind:value={passwordData.currentPassword}
          class="w-full px-4 py-3 rounded-xl border border-bright focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all"
          required
        />
      </div>
      <div>
        <label for="newPassword" class="block text-sm font-medium text-secondary mb-1">새 비밀번호</label>
        <input
          id="newPassword"
          type="password"
          bind:value={passwordData.newPassword}
          class="w-full px-4 py-3 rounded-xl border border-bright focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all"
          minlength="8"
          required
        />
      </div>
      <div>
        <label for="confirmPassword" class="block text-sm font-medium text-secondary mb-1">새 비밀번호 확인</label>
        <input
          id="confirmPassword"
          type="password"
          bind:value={passwordData.confirmPassword}
          class="w-full px-4 py-3 rounded-xl border border-bright focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all"
          minlength="8"
          required
        />
      </div>
      <div class="flex gap-3 pt-4">
        <button
          type="button"
          onclick={() => showPasswordModal = false}
          class="flex-1 py-3 bg-surface-hover text-secondary rounded-xl font-medium hover:bg-surface-hover transition-colors"
        >
          취소
        </button>
        <button
          type="submit"
          disabled={loading}
          class="flex-1 py-3 bg-gradient-to-r from-blue-600 to-cyan-500 text-white rounded-xl font-medium hover:shadow-lg transition-all disabled:opacity-50"
        >
          {loading ? "변경 중..." : "변경하기"}
        </button>
      </div>
    </form>
  </Modal>
</Layout>
